/**
 * ============================================================================
 * GAMIFICATION HOOK - useGamification
 * ============================================================================
 * Centralized gamification & leaderboard management
 * Version: v3.10.0-CLEAR-MAP
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useInspections } from './useInspections';
import * as api from '../services/apiService';
import { BACKEND_URL } from '../utils/constants';

const GamificationContext = createContext(null);

export const GamificationProvider = ({ children }) => {
  const { currentUser, submittedOnBehalfOf, getSubmittedByName } = useAuth();
  const { inspections } = useInspections();
  
  // ============================================================================
  // STATE
  // ============================================================================
  
  // Gamification display
  const [showGamification, setShowGamification] = useState(true);
  const [userGamificationStats, setUserGamificationStats] = useState(null);
  const [loadingGamificationStats, setLoadingGamificationStats] = useState(false);
  
  // Leaderboard data fetched server-side (submitted_by intact in DB)
  const [leaderboardStats, setLeaderboardStats] = useState([]);

  // Secure per-user score from /api/user/my-score (no PII in URLs)
  // Initialize from local cache so returning users see their score before re-auth
  const [myScore, setMyScore] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedMyScore');
      if (cached) return JSON.parse(cached);
    } catch (_) { /* ignore corrupt cache */ }
    return { points: 0, rank: 0 };
  });

  // Leaderboard pagination
  const [leaderboardPageSize, setLeaderboardPageSize] = useState(3);
  const [leaderboardCurrentPage, setLeaderboardCurrentPage] = useState(1);
  
  // User submissions modal
  const [showUserSubmissionsModal, setShowUserSubmissionsModal] = useState(false);
  const [selectedUserForSubmissions, setSelectedUserForSubmissions] = useState(null);
  
  // ============================================================================
  // USER SCORE — fetched from /api/user/my-score (no PII in URLs)
  // Works only with verified auth headers (Bearer token or server-side session token).
  // ============================================================================

  const fetchMyScore = async () => {
    const email = (submittedOnBehalfOf || '').trim();
    const name = (currentUser || '').trim();
    const primaryIdentity = email || name;
    if (!primaryIdentity) return;

    try {
      const headers = api.buildHeaders();
      const response = await fetch(`${BACKEND_URL}/api/user/my-score`, { headers });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        const score = { points: data.points || 0, rank: data.rank || 0 };
        setMyScore(score);
        try { localStorage.setItem('cachedMyScore', JSON.stringify(score)); } catch (_) {}
      }
    } catch (err) {
      console.error('SCORE-DEBUG: error=', err.message, err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      await fetchMyScore();
    };
    run();
    return () => { cancelled = true; };
  }, [currentUser, submittedOnBehalfOf]);

  // ============================================================================
  // LEADERBOARD DATA — only for the leaderboard page (protected, Tier 2/3)
  // ============================================================================

  useEffect(() => {
    let cancelled = false;

    const fetchLeaderboardStats = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/leaderboard/stats?period=all_time`, {
          headers: api.buildHeaders()
        });

        if (!response.ok) {
          if (!cancelled) setLeaderboardStats([]);
          return;
        }

        const data = await response.json();
        if (!cancelled) {
          setLeaderboardStats(data.success && Array.isArray(data.leaderboard) ? data.leaderboard : []);
        }
      } catch (_) {
        if (!cancelled) setLeaderboardStats([]);
      }
    };

    fetchLeaderboardStats();
    return () => { cancelled = true; };
  }, [currentUser, submittedOnBehalfOf]);

  // ============================================================================
  // POINTS CALCULATION
  // ============================================================================

  const getUserTotalPoints = () => myScore.points;

  const getUserRank = () => myScore.rank;

  /**
   * Return the full leaderboard array already calculated server-side.
   * Shape: [{ name, points, submissions, rank }, ...]
   */
  const getPointsLeaderboard = () => {
    return leaderboardStats.map(u => ({ name: u.name, points: u.points || 0 }));
  };

  /**
   * Get responsive leaderboard display count based on screen size
   */
  const getResponsiveLeaderboardCount = () => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 768) return 3; // Mobile: top 3
      if (width < 1024) return 4; // Tablet: top 4  
      return 4; // Desktop: top 4
    }
    return 4;
  };

  /**
   * Get paginated leaderboard data with real pagination
   */
  const getPaginatedLeaderboard = () => {
    const allUsers = getPointsLeaderboard();
    const startIndex = (leaderboardCurrentPage - 1) * leaderboardPageSize;
    const endIndex = startIndex + leaderboardPageSize;
    const currentPageUsers = allUsers.slice(startIndex, endIndex);

    return {
      topUsers: currentPageUsers,
      totalUsers: allUsers.length,
      currentPage: leaderboardCurrentPage,
      totalPages: Math.ceil(allUsers.length / leaderboardPageSize),
      hasPrevious: leaderboardCurrentPage > 1,
      hasNext: endIndex < allUsers.length
    };
  };
  
  // ============================================================================
  // API WRAPPER FUNCTIONS
  // ============================================================================
  
  /**
   * Fetch user gamification stats from backend
   */
  const fetchUserGamificationStats = async (userEmail) => {
    if (!userEmail || userEmail === 'Anonymous') return;
    
    setLoadingGamificationStats(true);
    try {
      const data = await api.fetchUserGamificationStats(userEmail);
      
      if (data.success && data.stats) {
        setUserGamificationStats(data.stats);
      } else {
        console.error('Failed to fetch gamification stats:', data.error);
      }
    } catch (err) {
      // Silently fail on 401 (expected when not authenticated)
      if (!err.message || !err.message.includes('401')) {
        console.error('Error fetching gamification stats:', err);
      }
    } finally {
      setLoadingGamificationStats(false);
    }
  };
  
  /**
   * Award gamification points
   */
  const awardGamificationPoints = async (userEmail, action = 'scan', showToast, playSound) => {
    try {
      const data = await api.awardGamificationPoints(userEmail, action);
      if (data.success) {
        // Show random confirmation phrase
        if (showToast) showToast(data.confirmation_phrase, 'success');
        
        // Play sound with debugging
        if (playSound) playSound('scan', 'gamification-award');
        
        return data;
      }
    } catch (err) {
      console.error('Failed to award points:', err);
    }
  };
  
  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // State
    showGamification,
    setShowGamification,
    userGamificationStats,
    setUserGamificationStats,
    loadingGamificationStats,
    setLoadingGamificationStats,
    leaderboardPageSize,
    setLeaderboardPageSize,
    leaderboardCurrentPage,
    setLeaderboardCurrentPage,
    showUserSubmissionsModal,
    setShowUserSubmissionsModal,
    selectedUserForSubmissions,
    setSelectedUserForSubmissions,
    
    // Functions
    getUserTotalPoints,
    getUserRank,
    refreshMyScore: fetchMyScore,
    getPointsLeaderboard,
    getResponsiveLeaderboardCount,
    getPaginatedLeaderboard,
    fetchUserGamificationStats,
    awardGamificationPoints,
    getSubmittedByName  // Pass through from useAuth
  };
  
  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

export default useGamification;

